import { Container, Grid } from '@mui/material'
import { AppBox, AppButton, AppDialog, AppDialogRefType, AppTypography, SimpleField, SimpleSwitch } from 'components'
import { SubAccountBillingStatus, SubAccountStatus } from 'globals/constants'
import { useMembershipHelper, useStates } from 'hooks'
import { forwardRef, useRef, useState } from 'react'
import { ShowAlert, SubAccountViewModel } from 'sdk'

interface Props {
  onStatusChange: (status: SubAccountStatus, subAccountId: string) => void
  onRemoveFromPlan: (subAccount: SubAccountViewModel) => void
}
export interface ManageSubAccountDialogRefType {
  openDialog?: (subAccount: SubAccountViewModel) => void
  closeDialog?: () => void
}
export const ManageSubAccountDialog = forwardRef((props: Props, ref) => {
  const [subAccount, setSubAccount] = useState<SubAccountViewModel | null>(null)
  const dialogRef = useRef<AppDialogRefType>({})
  const { CurrentMembership } = useMembershipHelper()
  const { States } = useStates()

  const handleAccountAccessChange = (status: boolean) => {
    dialogRef.current.closeDialog!()
    let _status = status ? SubAccountStatus.Enabled : SubAccountStatus.Disabled
    ShowAlert('question', `Are you sure you want to ${status ? 'Enable' : 'Disable'} this user's access?`).then((x) => {
      if (x.isConfirmed) {
        props.onStatusChange(_status, subAccount!.Id)
      }
    })
  }
  const openDialog = (subAccount: SubAccountViewModel) => {
    setSubAccount(subAccount)
    dialogRef.current.openDialog!()
  }
  const closeDialog = () => {
    dialogRef.current.closeDialog!()
  }
  if (ref) {
    ;(ref as any).current = { openDialog, closeDialog }
  }
  return (
    <AppDialog
      ref={dialogRef}
      maxWidth={'md'}
      title="Manage Your ParkSitesIO User"
      titleProps={{ textAlign: 'center' }}
      hideFooterCloseBtn={true}
    >
      <Container>
        <AppBox display={'flex'} flexDirection={'column'} gap={2}>
          <AppTypography color={'grey.500'} fontSize={14} fontWeight={500}>
            This user is a sub-account of your plan. Choose to remove them from your plan at the end of your current
            billing cycle, disable their login access, or both
          </AppTypography>
          <AppBox border={1} borderColor={'grey.300'} paddingX={2} paddingY={1} borderRadius={4}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <SimpleField disabled={true} label="Email" value={subAccount?.Email} />
              </Grid>
              <Grid item xs={6}>
                <AppTypography fontWeight={'bold'}>Account Access</AppTypography>
                <AppBox display={'flex'} gap={1} justifyContent={'center'} alignItems={'center'}>
                  <AppTypography>Disabled</AppTypography>
                  <SimpleSwitch
                    checked={subAccount?.Status === SubAccountStatus.Enabled}
                    size="medium"
                    disabled={subAccount?.Status === SubAccountStatus.Pending || !!!CurrentMembership()}
                    onChange={(event) => {
                      handleAccountAccessChange(event.target.checked)
                    }}
                  />
                  <AppTypography>Enabled</AppTypography>
                </AppBox>
              </Grid>
              <Grid item xs={6}>
                <SimpleField disabled={true} label="First Name" value={subAccount?.FirstName} />
              </Grid>
              <Grid item xs={6}>
                <SimpleField disabled={true} label="Last Name" value={subAccount?.LastName} />
              </Grid>
              <Grid item xs={12}>
                <SimpleField disabled={true} label="Street Address" value={subAccount?.Address} />
              </Grid>
              <Grid item xs={4}>
                <SimpleField disabled={true} label="City" value={subAccount?.City} />
              </Grid>
              <Grid item xs={4}>
                <SimpleField
                  disabled={true}
                  label="State"
                  value={States.find((x) => x.Id === subAccount?.State)?.Name ?? ''}
                />
              </Grid>
              <Grid item xs={4}>
                <SimpleField disabled={true} label="Zip" value={subAccount?.Zip} />
              </Grid>
            </Grid>
          </AppBox>
          <AppBox width={'100%'} justifyContent={'right'} display={'flex'} alignItems={'center'} gap={1}>
            <AppButton variant="outlined" sidePaddings={2} onClick={() => closeDialog()}>
              Cancel
            </AppButton>
            {subAccount?.BillingStatus === SubAccountBillingStatus.Billable && (
              <AppButton
                variant="contained"
                sidePaddings={2}
                color="error"
                onClick={() => props.onRemoveFromPlan(subAccount!)}
              >
                Remove From Plan
              </AppButton>
            )}
          </AppBox>
        </AppBox>
      </Container>
    </AppDialog>
  )
})
